import React, { useState, useRef } from 'react';
import { PhotoAlbum } from 'react-photo-album';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Button } from 'antd';

import { UnLinkIcon } from 'components/Icons';
import utils from 'utils/utils';
import { useSettings } from './Settings';
import SortablePhotoFrame from './SortablePhotoFrame';
import PhotoFrame from './PhotoFrame';

const DragablePhotoAlbum = ({ isOldGallery, photos, setPhotos, onLayoutChange, setTemporary }) => {
  const config = useSettings();
  const { options, containerProps, imageProps } = utils.albumConfig(config);
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);

  const handleImageLoad = (event) => {
    /* 
    REMARK :
    for image lacking height and width, calculate it based on natural width and height
    */

    const { naturalWidth, naturalHeight, id } = event.target;
    if (!naturalWidth || !naturalHeight || !id) {
      return;
    }

    setPhotos((prev) => {
      const newPhotos = [...prev];
      // find the photo and add width and height
      const photoIndex = newPhotos.findIndex((p) => p.id === id);
      newPhotos[photoIndex].width = naturalWidth;
      newPhotos[photoIndex].height = naturalHeight;

      onLayoutChange(newPhotos);
      return newPhotos;
    });
  };

  const renderedPhotos = useRef({});
  const [activeId, setActiveId] = useState();
  const activeIndex = activeId ? photos.findIndex((photo) => photo.id === activeId) : undefined;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 50, tolerance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = React.useCallback(({ active }) => setActiveId(active.id), []);

  const handleDragEnd = React.useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setPhotos((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newPhotos = arrayMove(items, oldIndex, newIndex);

        onLayoutChange(newPhotos);

        return newPhotos;
      });
    }

    setActiveId(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRemoveImage = (id) => {
    setPhotos((items) => {
      const newPhotos = [...items].filter((p) => p.id !== id);
      onLayoutChange(newPhotos);
      return newPhotos;
    });
  };

  const updateObjLink = (obj, link) => {
    if (link === '') {
      delete obj.link;
      // obj.link = '';
    } else {
      obj.link = link;
    }

    return obj;
  };

  const updateLink = (link, imageId) => {
    setPhotos((items) => {
      let newPhotos = [...items];
      if (imageId === 'all') {
        // remove link from all photos
        newPhotos = newPhotos.map((p) => {
          const newObj = { ...p };
          delete newObj.link;
          return newObj;
        });

        setTemporary({ images: newPhotos, config: { ...config, link } });
      } else {
        const photoIndex = newPhotos.findIndex((p) => p.id === imageId);
        if (photoIndex !== -1) {
          newPhotos[photoIndex] = updateObjLink(newPhotos[photoIndex], link);
        }
        setTemporary((prev) => ({ ...prev, images: newPhotos }));
      }

      return newPhotos;
    });
  };

  const renderPhoto = (props) => {
    const imageId = props.photo.id;
    // capture rendered photos for future use in DragOverlay
    renderedPhotos.current[imageId] = props;

    return (
      <SortablePhotoFrame
        {...{ updateLink, activeIndex, config, linkEditorOpen, setLinkEditorOpen }}
        onRemoveImage={() => onRemoveImage(imageId)}
        {...(isOldGallery ? { handleImageLoad } : {})}
        {...props}
      />
    );
  };

  if (photos.length === 0) {
    return null;
  }

  const isLinkAdded = config.link || photos.some((p) => p.link);

  const photoAlbum = (
    <PhotoAlbum
      photos={photos}
      {...options}
      spacing={config.spacing}
      renderPhoto={renderPhoto}
      componentsProps={{
        containerProps,
        imageProps,
      }}
    />
  );

  return (
    <div
      className="gallery-album"
      style={{
        '--gallery-spacing': `${config.spacing / 2 + 2}px`,
      }}>
      <div className="album-container">
        <div className="album-border">
          {linkEditorOpen ? (
            photoAlbum
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}>
              <SortableContext items={photos}>{photoAlbum}</SortableContext>

              <DragOverlay>
                {activeId && <PhotoFrame overlay {...renderedPhotos.current[activeId]} />}
              </DragOverlay>
            </DndContext>
          )}
        </div>
        {isLinkAdded && (
          <div className="button-wrapper">
            <Button
              className="medium-btn transparent-btn remove-all-links"
              icon={<UnLinkIcon />}
              onClick={() => updateLink('', 'all')}>
              Remove all links
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DragablePhotoAlbum;
